import { SUPPORTED_GENERATION_LANGUAGES } from "../../constants/LanguageSettingsConstants";
import { USE_DOCUMENT_LANGUAGE_FOR_CARD_LANGUAGE_SETTING } from "../../constants/GlobalTypesAndEnumsAndConstants";

interface ResolveVoiceOpeningReplyLanguageArgs {
  cardGenerationLanguage?: string | null;
  deckPageTextLanguage?: string | null;
  browserLanguages?: readonly string[] | null;
}

const DEFAULT_REPLY_LANGUAGE = "English";

export function resolveVoiceOpeningReplyLanguage({
  cardGenerationLanguage,
  deckPageTextLanguage,
  browserLanguages,
}: ResolveVoiceOpeningReplyLanguageArgs): string {
  const explicitLanguage = firstUsableLanguage([
    cardGenerationLanguage,
    deckPageTextLanguage,
  ]);
  if (explicitLanguage) return explicitLanguage;

  return (
    generationLanguageFromBrowserLanguages(browserLanguages ?? readBrowserLanguages()) ||
    DEFAULT_REPLY_LANGUAGE
  );
}

function firstUsableLanguage(values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed) continue;
    if (trimmed === USE_DOCUMENT_LANGUAGE_FOR_CARD_LANGUAGE_SETTING) continue;
    return trimmed;
  }
  return null;
}

function generationLanguageFromBrowserLanguages(
  browserLanguages: readonly string[],
): string | null {
  for (const browserLanguage of browserLanguages) {
    const normalizedBrowserLanguage = browserLanguage.trim().toLowerCase();
    if (!normalizedBrowserLanguage) continue;
    const baseCode = normalizedBrowserLanguage.split("-")[0];
    const match = SUPPORTED_GENERATION_LANGUAGES.find((language) => {
      const defaultCode = language.defaultCode.toLowerCase();
      const variants = (language.variants ?? []).map((variant) => variant.toLowerCase());
      return (
        normalizedBrowserLanguage === defaultCode ||
        normalizedBrowserLanguage === language.label.toLowerCase() ||
        normalizedBrowserLanguage === language.local_name.toLowerCase() ||
        variants.includes(normalizedBrowserLanguage) ||
        baseCode === defaultCode
      );
    });
    if (match) return match.label;
  }
  return null;
}

function readBrowserLanguages(): string[] {
  if (typeof navigator === "undefined") return [];
  return [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ].filter((language): language is string => typeof language === "string");
}
